<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";

const sections = [
  { title: "Python Solved Exercise", to: "/11th/computer/python/solved-exercise/", description: "Chapter 2 ke solved answers aur practice pages." },
  { title: "SDLC Solved Exercise", to: "/11th/computer/sdlc/exercise/", description: "Solved MCQs, short aur long-question flow ek jagah." },
  { title: "Algorithms Solved Exercise", to: "/11th/computer/algorithm/exercise/", description: "Algorithm MCQs, short aur long answers ka direct entry point." },
  { title: "Data Structures Solved Exercise", to: "/11th/computer/data-structures/exercise/", description: "Unit 4 ke objective aur descriptive revision links." },
  { title: "Data Analytics Solved Exercise", to: "/11th/computer/data-analytics/exercise/", description: "Unit 5 ke MCQs aur structured revision flow." },
  { title: "Digital Literacy Solved Exercise", to: "/11th/computer/digital-literacy/exercise/", description: "Unit 8 ke key MCQs aur answer routes." },
  { title: "Emerging Technologies Solved Exercise", to: "/11th/computer/emerging-tech/exercise/", description: "Unit 6 ke quick practice aur revision pages." },
  { title: "Legal & Ethics Solved Exercise", to: "/11th/computer/legal-ethics/exercise/", description: "Unit 7 ke MCQs aur short-answer revision links." },
  { title: "Entrepreneurship Solved Exercise", to: "/11th/computer/entrepreneurship/exercise/", description: "Unit 9 ke practice aur answer-building pages." },
];

const supportLinks = [
  { title: "Important Questions", to: "/11th/computer/important-questions/", description: "Most expected sawalat ko sath rakhne ke liye." },
  { title: "Practice Mode", to: "/11th/computer/practice/", description: "Mixed revision aur self-check ke liye." },
  { title: "Chapter Notes", to: "/11th/computer/notes/", description: "Exercise se pehle concept revise karne ke liye." },
];

const faqs = [
  {
    question: "11th class computer solved exercise ka kya faida hai?",
    answer:
      "Solved exercise se aapko ready answers, concept recap aur chapter-wise revision flow aik jagah milta hai. Yeh self-study ke liye useful hota hai.",
  },
  {
    question: "1st year computer solved exercise aur important questions me kya difference hai?",
    answer:
      "Solved exercise chapter ke broader practice flow ko cover karta hai, jab ke important questions sirf high-priority sawalat ko shortlist karte hain.",
  },
  {
    question: "Solved exercise ke baad next kya karna chahiye?",
    answer:
      "Solved exercise ke baad practice mode ya chapter-wise MCQs par jao taake recall aur speed dono improve ho sakein.",
  },
];

usePageSeo({
  title: "11th Class Computer Solved Exercise | Ustaad",
  description: "11th class computer solved exercise ke liye chapter-wise hub: Python, SDLC, Algorithms aur baqi chapters ke solved routes ek jagah.",
  keywords: "11th class computer solved exercise, first year computer solved exercise, chapter wise solved computer exercise",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Solved Exercise", item: "/11th/computer/solved-exercise/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Class Computer Solved Exercise Hub",
    items: sections.map((section) => ({ name: section.title, path: section.to })),
  }),
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Solved Exercise Hub</p>
      <h1 class="mt-3 text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">11th Class Computer Solved Exercise</h1>
      <p class="mt-4 max-w-3xl text-sm leading-7 text-slate-700 dark:text-slate-300">
        Yahan se chapter-wise solved exercise routes kholo. Agar aapko answers aur revision flow aik jagah chahiye ho, yeh page direct entry deta hai.
      </p>
      <p class="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
        Yeh page un students ke liye hai jo chapter ke answers aur practice route ko ek clean entry point se dekhna chahte hain.
      </p>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Start Here</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Choose your chapter</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <NuxtLink
          v-for="section in sections"
          :key="section.to"
          :to="section.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ section.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ section.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Related Prep</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Use solved exercise with revision pages</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="item in supportLinks"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">11th Computer Solved Exercise FAQs</h2>
      </div>
      <div class="mt-5 space-y-4">
        <article
          v-for="faq in faqs"
          :key="faq.question"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
        </article>
      </div>
    </section>
  </PageContainer>
</template>
